<template>
    <vx-modal class="max-w-lg" :visible="visible" @update:visible="emit('update:visible', $event)">
        <div v-if="!success" class="font-sans">
            <h2 class="text-xl font-bold text-slate-700 font-sans my-0">Apply Tags to Contacts</h2>

            <p class="text-slate-500 text-base font-sans mb-4">
                Specify tags that will be added to
                <strong>all contacts</strong> currently in the {{ props.segment.name }} Segment.
            </p>
            <div class="mb-8">
                <label class="font-bold text-slate-700 font-sans mb-1"> Select or Create Tags </label>
                <vx-selectable
                    multiple
                    taggable
                    placeholder="Enter a tag"
                    :clearable="false"
                    v-model="selectedTags"
                    :options="tags"
                    @search="onSearch"
                    @option:created="onCreated"
                    :reduce="(option) => (option?.value || option)?.toLowerCase()"
                >
                </vx-selectable>
            </div>
            <div class="flex space-x-2 justify-center">
                <vx-button @click="emit('update:visible', false)" type="button" size="lg" color="muted" class="w-20">
                    Cancel
                </vx-button>
                <vx-button
                    data-test="add-tags"
                    @click="tagContacts"
                    type="button"
                    size="lg"
                    color="primary"
                    class="grow"
                    :loading="submiting"
                >
                    Tag Contacts
                </vx-button>
            </div>
        </div>
        <div v-else>
            <div class="flex flex-col text-center items-center">
                <font-awesome-icon
                    widthAuto
                    class="text-green-600 text-5xl mb-4"
                    :icon="faCircleCheck"
                ></font-awesome-icon>
                <div class="mb-8">
                    <h3 class="text-3xl font-extrabold text-slate-700">Success</h3>
                    <p class="text-lg font-light text-slate-600">Contacts have successfully been tagged.</p>
                </div>
                <vx-button @click="emit('update:visible', false)" color="primary" size="lg" block> Ok </vx-button>
            </div>
        </div>
    </vx-modal>
</template>

<script setup>
import { ref, watch } from 'vue';
import { segmentsClient, tagsClient } from '~/services';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';

import { useToasted } from '../../../composables/useToasted';
import { VxButton, VxModal, VxSelectable } from '@voxie/frontend-components';
import debounce from '~/utils/debounce';

const props = defineProps({
    segment: {
        type: Object,
        required: true,
    },
    visible: Boolean,
});

const toasted = useToasted();

const teamId = Spark.state.currentTeam.id;

const emit = defineEmits(['update:visible']);

const success = ref(false);
const submiting = ref(false);

const tags = ref([]);
const selectedTags = ref([]);

const onSearch = (search, loading) => {
    loading(true);
    debouncedSearch(loading, search);
};

const runSearch = async (loading = () => {}, search = '') => {
    loading(true);

    try {
        const response = await tagsClient.tagsAutocomplete(teamId, {
            query: search,
        });

        tags.value = response.data.data.map((tag) => tag.value.toLowerCase());
    } catch (e) {
        console.error(e);
    } finally {
        loading(false);
    }
};

const onCreated = async (tagName) => {
    tags.value = [tagName.toLowerCase(), ...tags.value];
};

const debouncedSearch = debounce(runSearch, 350);

// act as on mounted as well
watch(
    () => props.visible,
    (visible) => {
        success.value = false;
        submiting.value = false;
        selectedTags.value = [];

        if (visible) {
            runSearch();
        }
    },
    {
        immediate: true,
    }
);

const tagContacts = async () => {
    if (submiting.value || !selectedTags.value.length) {
        return;
    }

    submiting.value = true;
    try {
        await segmentsClient.tagContacts({ teamId }, props.segment.id, {
            tags: selectedTags.value,
        });
        success.value = true;
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    } finally {
        submiting.value = false;
    }
};
</script>
